import { PostgrestClient } from "@supabase/postgrest-js";
import { useCallback, useEffect, useState } from "react";
import { ProgressState } from "../../../../components/ProgressDialog";
import { useSnackbar } from "../../../../containers/SnackbarContainer";
import { fetchAircraftWithCache } from "../../../../hooks/api";
import { PlacementService } from "../../../../services/PlacementService";
import {
  Aircraft,
  FBO,
  PlacementWithLocation,
  Ramp,
  Stack,
} from "../../../../types";
import { useParkingConfig } from "../../TemporalTest/hooks/useParkingConfig";
import {
  getDecisionLog,
  parkingAlgorithm,
} from "../../TemporalTest/parkingAlgorithm";
import {
  aircraftToSizeCategory,
  buildSpotsFromRamp,
  buildZonesFromRamp,
  convertPlacementToPositionSegments,
  dateToTimeBucket,
  runIncrementalParkingOnRamp,
  runSnapshotParkingOnRamp,
  SnapshotPlacementResult,
  TemporalPlacementResult,
  timeBucketToDate,
} from "../../TemporalTest/temporalRampIntegration";
import { findSpotForPosition } from "../../TemporalTest/positionMapping";
import { ParkingRequest } from "../../TemporalTest/types";
import { captureTestData } from "../../TemporalTest/testDataCapture";
import { groupPlacementsByTrip } from "../utils/groupPlacementsByTrip";
import { GroupedTrip } from "../types";
import { detectPartialPlacements } from "../utils/detectPartialPlacements";
import { getSpotForPositionLegacy } from "../../../../utils/spots";

export function useAutoPark(
  ramp: Ramp,
  stack: Stack,
  setStack: (stack: Stack) => void,
  isScheduledView?: boolean,
  placementService?: PlacementService,
  currentTime?: Date,
  postgrest?: PostgrestClient,
  fbo?: FBO,
  onScheduledUpdate?: () => void
) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [lastResult, setLastResult] = useState<
    SnapshotPlacementResult | TemporalPlacementResult | null
  >(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applyProgress, setApplyProgress] = useState<ProgressState>({
    current: 0,
    total: 0,
    currentItem: "",
  });
  const snackbar = useSnackbar();

  // Time range state for scheduled view
  // Use currentTime from scheduled view if available, otherwise default to now
  const now = currentTime || new Date();
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const [timeRange, setTimeRange] = useState<{ start: Date; end: Date }>({
    start: now,
    end: tomorrow,
  });
  const [tripCount, setTripCount] = useState(0);
  const [placements, setPlacements] = useState<PlacementWithLocation[]>([]);
  const [groupedTrips, setGroupedTrips] = useState<GroupedTrip[]>([]);
  const [selectedTripIds, setSelectedTripIds] = useState<Set<string>>(
    new Set()
  );

  // Sync timeRange with currentTime when slider moves or dialog opens
  useEffect(() => {
    if (currentTime) {
      const newStart = currentTime;
      const newEnd = new Date(currentTime.getTime() + 24 * 60 * 60 * 1000);
      setTimeRange({ start: newStart, end: newEnd });
    }
  }, [currentTime, dialogOpen]);

  // Fetch placements when time range changes (scheduled view only)
  useEffect(() => {
    if (!isScheduledView || !placementService || !ramp.id) {
      return;
    }

    const fetchPlacements = async () => {
      try {
        const fetchedPlacements =
          await placementService.getPlacementsInTimeRange(
            timeRange.start,
            timeRange.end,
            ramp.id,
            "ramp"
          );

        // Fetch aircraft data for all placements
        const aircraftIds = Array.from(
          new Set(
            fetchedPlacements
              .map((p) => p.aircraft_id)
              .filter((id): id is string => id != null)
          )
        );

        const aircrafts = await fetchAircraftWithCache(postgrest, aircraftIds, fbo);
        const aircraftMap = new Map<string, Aircraft>();
        aircrafts.forEach((aircraft) => {
          aircraftMap.set(aircraft.id, aircraft);
        });

        // Enrich placements with aircraft data
        const enrichedPlacements = fetchedPlacements.map((p) => {
          const aircraft = p.aircraft_id ? aircraftMap.get(p.aircraft_id) : undefined;
          return {
            ...p,
            aircraft_make: aircraft?.make,
            aircraft_model: aircraft?.model,
          };
        });

        setPlacements(enrichedPlacements);

        // Group placements by trip_id for display
        const grouped = groupPlacementsByTrip(enrichedPlacements);
        setGroupedTrips(grouped);
        setTripCount(grouped.length);

        // Default all trips to selected
        const allTripIds = new Set(enrichedPlacements.map((p) => p.trip_id));
        setSelectedTripIds(allTripIds);
      } catch (error) {
        console.error(
          "[AutoPark useEffect] Failed to fetch placements:",
          error
        );
        setPlacements([]);
        setGroupedTrips([]);
        setTripCount(0);
        setSelectedTripIds(new Set());
      }
    };

    fetchPlacements();
  }, [
    isScheduledView,
    placementService,
    ramp.id,
    timeRange.start,
    timeRange.end,
    postgrest,
    fbo,
  ]);

  // Use shared parking config hook with AutoPark defaults
  const parkingConfig = useParkingConfig({
    bufferMinutes: 15, // Standard buffer
    plannedMoveCap: 0, // AutoPark default: no planned moves
    allowTrapping: true, // Allow temporal trapping (not a true trap if departure times allow egress)
    egressWeight: 1.0,
    wasteWeight: 1.0,
    structuralWeight: 0.1,
    sortingStrategy: "size-constrained",
    zonePriorityWeight: 1.0,
  });

  const handleOpenDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleOpenLogDialog = useCallback(() => {
    setLogDialogOpen(true);
  }, []);

  const handleCloseLogDialog = useCallback(() => {
    setLogDialogOpen(false);
  }, []);

  // Snapshot parking (for CurrentRamp view - all aircraft at one moment)
  const runSnapshotAutoPark = useCallback(() => {
    try {
      // Run the placement algorithm asynchronously to prevent UI blocking
      setTimeout(() => {
        const startTime = performance.now();
        try {
          // Clear all aircraft positions to start fresh
          const cleanStack = {
            ...stack,
            tenants: stack.tenants.map((tenant) => ({
              ...tenant,
              position: {
                ...tenant.position,
                x: null,
                y: null,
                angle: null,
              },
            })),
          };

          const result = runSnapshotParkingOnRamp(ramp, cleanStack, {
            allowTrapping: parkingConfig.config.allowTrapping,
            egressWeight: parkingConfig.config.egressWeight,
            wasteWeight: parkingConfig.config.wasteWeight,
            structuralWeight: parkingConfig.config.structuralWeight,
            sortingStrategy: parkingConfig.config.sortingStrategy,
            zonePriorityWeight: parkingConfig.config.zonePriorityWeight,
          });

          // Calculate elapsed time
          const elapsedMs = performance.now() - startTime;

          // Add elapsedMs to statistics
          const resultWithTiming = {
            ...result,
            statistics: {
              ...result.statistics,
              elapsedMs,
            },
          };

          // Update the stack with new positions
          setStack(resultWithTiming.updatedStack);

          // Store the result for decision log viewer
          setLastResult(resultWithTiming);
          const severity =
            result.statistics.unplacedAircraft === 0
              ? "success"
              : result.statistics.placedAircraft > 0
              ? "warning"
              : "error";

          snackbar.notify({
            text: `AutoPark: Placed ${result.statistics.placedAircraft}/${
              result.statistics.totalAircraft
            } aircraft in ${(elapsedMs / 1000).toFixed(2)}s`,
            severity,
          });

          // Log summary for user feedback in development
          if (process.env.NODE_ENV !== "production") {
            console.log(
              `AutoPark (Snapshot): Placed ${
                result.statistics.placedAircraft
              }/${result.statistics.totalAircraft} aircraft in ${(
                elapsedMs / 1000
              ).toFixed(2)}s`
            );

            // Log decision log for algorithm debugging
            if (result.decisionLog && result.decisionLog.length > 0) {
              console.log(
                "AutoPark Decision Log:\n" +
                  JSON.stringify(result.decisionLog, null, 2)
              );
            }
          }
        } catch (error) {
          if (process.env.NODE_ENV !== "production") {
            console.error("Error running Snapshot AutoPark:", error);
          }
        }
      }, 0);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error running Snapshot AutoPark:", error);
      }
    }
  }, [parkingConfig.config, ramp, stack, setStack, snackbar]);

  // Temporal parking (for ScheduledRampView - aircraft have arrival/departure times)
  const runTemporalAutoPark = useCallback(async () => {
    const startTime = performance.now();
    try {
      // Validate we have the required dependencies
      if (!postgrest || !fbo) {
        console.error("[Temporal AutoPark] Missing postgrest or fbo");
        snackbar.notify({
          text: "AutoPark not available: missing required dependencies",
          severity: "error",
        });
        return;
      }

      // Filter to selected trips (unlocked only - locked will be added separately)
      const selectedPlacements = placements.filter((p) =>
        selectedTripIds.has(p.trip_id) && !p.is_locked
      );

      // Also get locked placements in time range to show as fixed constraints
      const lockedPlacements = placements.filter((p) => p.is_locked);

      if (selectedPlacements.length === 0 && lockedPlacements.length === 0) {
        console.warn("[Temporal AutoPark] No trips selected");
        snackbar.notify({
          text: "No trips selected. Please select at least one trip to park.",
          severity: "warning",
        });
        return;
      }

      console.log(
        `[Temporal AutoPark] Placing ${selectedPlacements.length} selected trips`
      );

      // Calculate horizon in hours
      const horizonHours =
        (timeRange.end.getTime() - timeRange.start.getTime()) /
        (1000 * 60 * 60);

      console.log(
        `[Temporal AutoPark] Time range: ${timeRange.start.toISOString()} to ${timeRange.end.toISOString()} (${horizonHours.toFixed(
          1
        )}h)`
      );

      // Extract unique aircraft IDs from placements
      const aircraftIds = Array.from(
        new Set(
          selectedPlacements
            .map((p) => p.aircraft_id)
            .filter((id): id is string => id != null)
        )
      );

      console.log(
        `[Temporal AutoPark] Fetching ${aircraftIds.length} aircraft...`
      );

      // Fetch real aircraft using the cache
      const aircrafts = await fetchAircraftWithCache(
        postgrest,
        aircraftIds,
        fbo
      );

      // Create aircraft lookup map
      const aircraftMap = new Map<string, Aircraft>();
      aircrafts.forEach((aircraft) => {
        aircraftMap.set(aircraft.id, aircraft);
      });

      console.log(
        `[Temporal AutoPark] Fetched ${aircrafts.length} aircraft`
      );

      // Fetch trip-level arrival/departure dates for BOTH selected AND locked trips
      const uniqueTripIds = Array.from(
        new Set([...selectedPlacements, ...lockedPlacements].map((p) => p.trip_id))
      );

      console.log(
        `[Temporal AutoPark] Fetching dates for ${uniqueTripIds.length} unique trips (${selectedPlacements.length} unlocked + ${lockedPlacements.length} locked placements)...`
      );

      const { data: trips, error: tripError } = await postgrest
        .from("trip")
        .select("id, arrival_date, departure_date")
        .in("id", uniqueTripIds);

      if (tripError) {
        console.error("[Temporal AutoPark] Failed to fetch trip dates:", tripError);
        snackbar.notify({
          text: "Failed to load trip data",
          severity: "error",
        });
        return;
      }

      // Build trip date lookup map
      const tripDateMap = new Map(
        trips?.map((t) => [
          t.id,
          {
            arrival_date: new Date(t.arrival_date),
            departure_date: new Date(t.departure_date),
          },
        ]) || []
      );

      console.log(
        `[Temporal AutoPark] Fetched dates for ${trips?.length || 0} trips`
      );

      // Deduplicate by trip_id (one placement per trip, not per segment!)
      const tripToPlacementMap = new Map<string, PlacementWithLocation>();
      selectedPlacements.forEach((placement) => {
        if (!tripToPlacementMap.has(placement.trip_id)) {
          tripToPlacementMap.set(placement.trip_id, placement);
        }
      });

      console.log(
        `[Temporal AutoPark] Deduplicated ${selectedPlacements.length} placement segments ` +
        `to ${tripToPlacementMap.size} unique trips`
      );

      console.log(
        `[Temporal AutoPark] Building parking requests...`
      );

      // Build ONE ParkingRequest per trip (not per segment!)
      const requests: ParkingRequest[] = Array.from(tripToPlacementMap.values())
        .map((placement) => {
          // Get the real aircraft object
          const aircraft = placement.aircraft_id
            ? aircraftMap.get(placement.aircraft_id)
            : undefined;

          if (!aircraft) {
            console.warn(
              `[Temporal AutoPark] Missing aircraft for trip ${placement.trip_id} (aircraft_id: ${placement.aircraft_id}), skipping`
            );
            return null;
          }

          // Get trip-level dates (not segment times!)
          const tripDates = tripDateMap.get(placement.trip_id);
          if (!tripDates) {
            console.warn(
              `[Temporal AutoPark] Missing trip dates for ${placement.trip_id}, skipping`
            );
            return null;
          }

          const sizeCategory = aircraftToSizeCategory(aircraft);

          // Use TRIP-LEVEL arrival/departure times (not segment times!)
          const arrivalBucket = dateToTimeBucket(
            tripDates.arrival_date,
            timeRange.start
          );
          const departureBucket = dateToTimeBucket(
            tripDates.departure_date,
            timeRange.start
          );

          // Clamp to planning window [0, horizonBuckets)
          const maxBucket = horizonHours * 4; // 15-min buckets per hour
          const clampedArrival = Math.max(0, arrivalBucket);
          const clampedDeparture = Math.min(departureBucket, maxBucket);

          // Skip trips that don't overlap the window at all
          if (clampedDeparture <= 0 || clampedArrival >= maxBucket) {
            console.warn(
              `[Temporal AutoPark] Trip ${placement.trip_id} (${placement.tail_number}) outside planning window, skipping`
            );
            return null;
          }

          // Window-aware staging: only stage if arrival/departure happens in window
          const tripArrivesInWindow = tripDates.arrival_date >= timeRange.start;
          const tripDepartsInWindow = tripDates.departure_date <= timeRange.end;

          return {
            id: placement.trip_id, // ✅ Use trip_id as unique ID (not placement.id!)
            tail_number: placement.tail_number || "Unknown",
            sizeCategory,
            arrivalBucket: clampedArrival,
            departureBucket: clampedDeparture,
            aircraft,
            trip_id: placement.trip_id,
            placement_source: placement.placement_source || 'auto',
            is_locked: placement.is_locked || false,
            metadata: {
              tripArrivesInWindow,
              tripDepartsInWindow,
            },
          };
        })
        .filter((r): r is ParkingRequest => r !== null);

      console.log(
        `[Temporal AutoPark] Built ${requests.length} parking requests`
      );

      // Extract spots and zones from ramp
      const spots = buildSpotsFromRamp(ramp, horizonHours);
      const zones = buildZonesFromRamp(ramp);

      const algorithmStart = performance.now();

      // Call parkingAlgorithm directly (bypassing runTemporalParkingOnRamp)
      const placement = parkingAlgorithm(
        requests,
        spots,
        parkingConfig.config.bufferMinutes || 15,
        parkingConfig.config.plannedMoveCap || 0,
        parkingConfig.config.allowTrapping,
        parkingConfig.config.egressWeight,
        parkingConfig.config.wasteWeight,
        0.1, // structuralWeight (fixed for temporal)
        "arrival", // sorting strategy (fixed for temporal)
        horizonHours,
        // shapes parameter removed - using spot.shape
        false, // useMaskOptimization
        false, // validateMasks
        parkingConfig.config.zonePriorityWeight,
        zones,
        parkingConfig.config.min_dwell_weight,
        0.0, // stickinessWeight (not used in temporal mode)
        parkingConfig.config.enableArrivalStaging,
        parkingConfig.config.enableDepartureStaging,
        parkingConfig.config.arrivalStagingDwellMinutes,
        parkingConfig.config.departureStagingDwellMinutes,
        parkingConfig.config.quickTurnThresholdMinutes
      );

      const decisionLog = getDecisionLog();

      // Capture test data for duplicate detection (only logs if duplicates found)
      captureTestData(
        requests,
        spots,
        zones,
        {
          bufferMinutes: parkingConfig.config.bufferMinutes || 15,
          plannedMoveCap: parkingConfig.config.plannedMoveCap || 0,
          allowTrapping: parkingConfig.config.allowTrapping,
          egressWeight: parkingConfig.config.egressWeight,
          wasteWeight: parkingConfig.config.wasteWeight,
          zonePriorityWeight: parkingConfig.config.zonePriorityWeight,
          horizonHours,
        },
        placement
      );

      // Calculate statistics
      const placedCount = Object.keys(placement).length;
      const unplacedCount = requests.length - placedCount;
      const algorithmElapsed = performance.now() - algorithmStart;
      console.log(
        `[Temporal AutoPark] Placement complete (${algorithmElapsed.toFixed(
          0
        )}ms):`,
        placement
      );

      // Calculate average moves
      let totalMoves = 0;
      let placementsWithSegments = 0;
      Object.values(placement).forEach((p) => {
        if ("segments" in p && "moveCount" in p) {
          totalMoves += p.moveCount;
          placementsWithSegments++;
        }
      });
      const averageMoves =
        placementsWithSegments > 0 ? totalMoves / placementsWithSegments : 0;

      // Calculate total elapsed time
      const totalElapsedMs = performance.now() - startTime;

      // Detect partial placements (aircraft placed for only part of their trip)
      const partialPlacements = detectPartialPlacements(
        placement,
        requests,
        timeRange.start
      );
      const partiallyPlacedCount = partialPlacements.length;

      // Update placement_source to 'auto' for successfully placed UNLOCKED aircraft
      // Locked aircraft keep their original placement_source (manual) to show they were user-placed
      const updatedRequests = requests.map(req => {
        const wasPlaced = placement[req.id] !== undefined;
        const shouldMarkAsAuto = wasPlaced && !req.is_locked;
        return shouldMarkAsAuto ? { ...req, placement_source: 'auto' as const } : req;
      });

      // Build ParkingRequest objects for LOCKED aircraft to show them in timeline
      // Use same logic as unlocked aircraft building
      const lockedTripIds = new Set(lockedPlacements.map(p => p.trip_id));
      const lockedTripMap = new Map<string, typeof lockedPlacements[0]>();

      lockedPlacements.forEach((p) => {
        if (!lockedTripMap.has(p.trip_id)) {
          lockedTripMap.set(p.trip_id, p);
        }
      });

      const lockedRequests: ParkingRequest[] = Array.from(lockedTripMap.values())
        .map((placement) => {
          const aircraft = placement.aircraft_id ? aircraftMap.get(placement.aircraft_id) : undefined;
          if (!aircraft) return null;

          const tripDates = tripDateMap.get(placement.trip_id);
          if (!tripDates) return null;

          const sizeCategory = aircraftToSizeCategory(aircraft);
          const arrivalBucket = dateToTimeBucket(tripDates.arrival_date, timeRange.start);
          const departureBucket = dateToTimeBucket(tripDates.departure_date, timeRange.start);
          const maxBucket = horizonHours * 4;
          const clampedArrival = Math.max(0, arrivalBucket);
          const clampedDeparture = Math.min(departureBucket, maxBucket);

          if (clampedDeparture <= 0 || clampedArrival >= maxBucket) return null;

          return {
            id: placement.trip_id,
            tail_number: placement.tail_number || "Unknown",
            sizeCategory,
            arrivalBucket: clampedArrival,
            departureBucket: clampedDeparture,
            aircraft,
            trip_id: placement.trip_id,
            placement_source: placement.placement_source || 'manual',  // Keep as manual
            is_locked: true,  // All locked
            metadata: {
              tripArrivesInWindow: tripDates.arrival_date >= timeRange.start,
              tripDepartsInWindow: tripDates.departure_date <= timeRange.end,
            },
          };
        })
        .filter((r): r is ParkingRequest => r !== null);

      // Add synthetic placement entries for locked aircraft (using their current spot from DB)
      // This allows them to appear in the timeline in their locked positions
      const combinedPlacement = { ...placement };
      lockedRequests.forEach((lockedReq) => {
        // Find the locked placement's current spot from database
        const lockedDbPlacement = lockedPlacements.find(p => p.trip_id === lockedReq.id);
        if (lockedDbPlacement && lockedDbPlacement.x != null && lockedDbPlacement.y != null) {
          // Map position to spot ID using spot detection
          const spotId = findSpotForPosition(
            {
              x: lockedDbPlacement.x,
              y: lockedDbPlacement.y,
            },
            ramp.shapes,
            ramp.depth
          );

          if (spotId) {
            // Add synthetic placement showing locked aircraft in its current spot
            combinedPlacement[lockedReq.id] = {
              spotId: spotId,
              orientationId: 'default',  // Locked aircraft keep their manual orientation
            };
          }
        }
      });

      // Combine locked and unlocked requests for timeline display
      const allRequests = [...updatedRequests, ...lockedRequests];

      // Create result object matching TemporalPlacementResult interface
      const result: TemporalPlacementResult = {
        placement: combinedPlacement,  // Use combined placement (includes locked aircraft)
        decisionLog,
        updatedStack: stack, // Don't update stack for now (just show results)
        statistics: {
          totalAircraft: allRequests.length,  // Total includes locked + unlocked
          placedAircraft: placedCount + lockedRequests.length,  // Placed = algo results + locked (already placed)
          unplacedAircraft: unplacedCount,
          partiallyPlacedAircraft: partiallyPlacedCount,
          averageMoves,
          elapsedMs: totalElapsedMs,
        },
        temporalData: {
          spots,
          requests: allRequests,  // Use combined requests (unlocked + locked)
          zones,
          horizonHours,
          startTime: timeRange.start,
          partialPlacements,
        },
      };

      // Store the result for decision log viewer
      setLastResult(result);

      // Auto-open the decision log dialog to show results
      handleOpenLogDialog();

      // Log summary
      console.log(
        `[Temporal AutoPark] Placed ${placedCount}/${requests.length} aircraft`
      );

      // Show snackbar notification
      const severity =
        unplacedCount === 0 ? "success" : placedCount > 0 ? "warning" : "error";

      snackbar.notify({
        text: `AutoPark: Placed ${placedCount}/${
          requests.length
        } aircraft in ${(algorithmElapsed / 1000).toFixed(2)}s`,
        severity: severity,
      });
    } catch (error) {
      console.error("[Temporal AutoPark] Error:", error);
      snackbar.notify({
        text: `AutoPark failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        severity: "error",
      });
    }
  }, [
    ramp,
    stack,
    placements,
    selectedTripIds,
    timeRange,
    parkingConfig.config,
    snackbar,
    postgrest,
    fbo,
  ]);

  const handleAutoPark = useCallback(() => {
    // Close dialog first
    setDialogOpen(false);

    // Check if we have aircraft to place (snapshot mode only - scheduled mode uses selectedPlacements)
    if (!isScheduledView && (!stack.tenants || stack.tenants.length === 0)) {
      return;
    }

    // Check if we have spots
    const spots = ramp.shapes?.filter((s) => s.type === "Spot") || [];

    if (spots.length === 0) {
      return;
    }

    // Branch on scheduled view mode
    const isScheduledMode =
      isScheduledView && timeRange && placements.length > 0;

    if (isScheduledMode) {
      runTemporalAutoPark();
    } else {
      runSnapshotAutoPark();
    }
  }, [
    isScheduledView,
    timeRange,
    placements,
    ramp,
    stack,
    runTemporalAutoPark,
    runSnapshotAutoPark,
    selectedTripIds,
  ]);

  // Handle incremental parking for selected aircraft
  const handleIncrementalPark = useCallback(
    (aircraftId?: string) => {
      if (!aircraftId) {
        return;
      }

      // Find aircraft by ID (not by selected flag, since it might be toggled off)
      const selectedAircraft = stack.tenants.find((t) => t.id === aircraftId);

      if (!selectedAircraft) {
        return;
      }

      // Check if we have spots
      const spots = ramp.shapes?.filter((s) => s.type === "Spot") || [];
      if (spots.length === 0) {
        return;
      }

      try {
        const result = runIncrementalParkingOnRamp(
          ramp,
          stack,
          selectedAircraft.id,
          {
            allowTrapping: parkingConfig.config.allowTrapping,
            egressWeight: parkingConfig.config.egressWeight,
            wasteWeight: parkingConfig.config.wasteWeight,
            structuralWeight: parkingConfig.config.structuralWeight,
            sortingStrategy: parkingConfig.config.sortingStrategy,
            zonePriorityWeight: parkingConfig.config.zonePriorityWeight,
          }
        );

        // Update stack with new position
        setStack(result.updatedStack);

        // Store result for decision log viewer
        setLastResult(result);

        // Create aircraft display name with fallback
        const aircraftName =
          selectedAircraft.tail_number ||
          selectedAircraft.aircraft.model ||
          "Aircraft";

        // Show snackbar notification
        if (result.statistics.placedAircraft === 1) {
          // Success - extract zone and spot information
          const placement = result.placement[selectedAircraft.id];
          if (placement) {
            const spotId =
              "spotId" in placement
                ? placement.spotId
                : placement.segments[0].spotId;
            const spotShape = ramp.shapes?.find(
              (s) => s.id === spotId && s.type === "Spot"
            );
            const spotName = spotShape?.text || spotId;

            // Get zone information
            const zoneId = (spotShape?.metadata as any)?.zoneId;
            const zoneShape = zoneId
              ? ramp.shapes?.find((s) => s.id === zoneId && s.type === "Zone")
              : null;
            const zoneName =
              zoneShape?.text ||
              (zoneId && zoneId !== "default" ? zoneId : "Default Zone");

            snackbar.notify({
              text: `${aircraftName} placed in ${zoneName} → ${spotName}`,
              severity: "success",
            });
          } else {
            // Fallback if we can't extract placement details
            snackbar.notify({
              text: `${aircraftName} placed successfully`,
              severity: "success",
            });
          }
        } else {
          // Failure - no available spots
          snackbar.notify({
            text: `No available spots for ${aircraftName}`,
            severity: "warning",
          });
        }
      } catch (error) {
        console.error("[Incremental Park] Error:", error);
      }
    },
    [ramp, stack, setStack, parkingConfig.config, snackbar, setLastResult]
  );

  // Handle schedule changes from GANTT drag-and-drop
  const handleScheduleChanged = useCallback(
    (changes: Array<{
      aircraftId: string;
      tailNumber: string;
      originalSpotId?: string;
      newSpotId?: string;
      newArrivalBucket?: number;
      newDepartureBucket?: number;
      segmentIndex?: number | null;
    }>) => {
      if (!lastResult || !("temporalData" in lastResult)) {
        return;
      }

      // Clone BOTH placement and requests (we may need to update both)
      const updatedPlacement = { ...lastResult.placement };
      const updatedRequests = lastResult.temporalData.requests ? [...lastResult.temporalData.requests] : [];

      changes.forEach((change) => {
        const { aircraftId, newSpotId, newArrivalBucket, newDepartureBucket, segmentIndex } = change;
        const currentPlacement = updatedPlacement[aircraftId];

        if (!currentPlacement) {
          return;
        }

        // Determine if this is a single or segmented placement
        const isSinglePlacement = "spotId" in currentPlacement;
        const isSegmented = "segments" in currentPlacement;

        // === HANDLE TIME CHANGES ===
        if (newArrivalBucket !== undefined || newDepartureBucket !== undefined) {
          if (isSinglePlacement) {
            // Single placement: Update request's arrivalBucket/departureBucket
            const requestIndex = updatedRequests.findIndex(r => r.id === aircraftId);
            if (requestIndex !== -1) {
              updatedRequests[requestIndex] = {
                ...updatedRequests[requestIndex],
                ...(newArrivalBucket !== undefined && { arrivalBucket: newArrivalBucket }),
                ...(newDepartureBucket !== undefined && { departureBucket: newDepartureBucket }),
              };
            }
          } else if (isSegmented && segmentIndex !== null && segmentIndex !== undefined) {
            // Segmented placement: Update specific segment's startBucket/endBucket
            const segments = [...currentPlacement.segments];
            if (segmentIndex < segments.length) {
              segments[segmentIndex] = {
                ...segments[segmentIndex],
                ...(newArrivalBucket !== undefined && { startBucket: newArrivalBucket }),
                ...(newDepartureBucket !== undefined && { endBucket: newDepartureBucket }),
              };
              updatedPlacement[aircraftId] = {
                ...currentPlacement,
                segments,
              };
            }
          }
        }

        // === HANDLE SPOT CHANGES ===
        if (newSpotId) {
          if (isSinglePlacement) {
            // Single placement - update spot
            updatedPlacement[aircraftId] = {
              ...currentPlacement,
              spotId: newSpotId,
            };
          } else if (isSegmented && segmentIndex !== null && segmentIndex !== undefined) {
            // Segmented placement - update ONLY the specific segment
            const segments = [...currentPlacement.segments];
            if (segmentIndex < segments.length) {
              segments[segmentIndex] = {
                ...segments[segmentIndex],
                spotId: newSpotId,
              };
              updatedPlacement[aircraftId] = {
                ...currentPlacement,
                segments,
              };
            }
          }
        }
      });

      // Update lastResult with modified placement AND requests
      setLastResult({
        ...lastResult,
        placement: updatedPlacement,
        temporalData: {
          ...lastResult.temporalData,
          requests: updatedRequests,
        },
      });
    },
    [lastResult, setLastResult]
  );

  // Handle park next: place selected aircraft or largest aircraft without spot
  const handleParkNext = useCallback(() => {
    // Helper: Check if aircraft has a spot assigned
    const hasSpot = (tenant: typeof stack.tenants[0]): boolean => {
      if (tenant.position.x === null || tenant.position.y === null) {
        return false; // Unplaced = no spot
      }

      // Build legacy spots from ramp shapes
      const spotShapes = ramp.shapes?.filter((s) => s.type === "Spot") || [];
      const legacySpots = spotShapes.map((shape) => ({
        points: shape.points,
        referencePoints: shape.reference_points || [],
      }));

      // Transform position from top-left origin to bottom-left origin
      // (getSpotForPositionLegacy expects bottom-left origin coordinates)
      const transformedPosition = {
        x: tenant.position.x,
        y: ramp.depth - tenant.position.y, // Y-flip transformation
        angle: tenant.position.angle,
      };

      // Use getSpotForPositionLegacy to check if aircraft is in a spot
      const result = getSpotForPositionLegacy(
        tenant,
        transformedPosition, // Use transformed position
        legacySpots,
        {}
      );

      return result !== undefined; // Has spot if result is not undefined
    };

    // 1. Check if there's a selected aircraft
    const selectedAircraft = stack.tenants.find((t) => t.selected);

    if (selectedAircraft) {
      // Selected aircraft exists - check if it has a spot
      if (hasSpot(selectedAircraft)) {
        snackbar.notify({
          text: `${
            selectedAircraft.tail_number || "Aircraft"
          } is already in a spot`,
          severity: "info",
        });
        return;
      }

      // Park the selected aircraft
      handleIncrementalPark(selectedAircraft.id);
      return;
    }

    // 2. No selection - find aircraft without spots
    const aircraftWithoutSpots = stack.tenants.filter((t) => !hasSpot(t));

    if (aircraftWithoutSpots.length === 0) {
      snackbar.notify({
        text: "No unplaced aircraft available",
        severity: "info",
      });
      return;
    }

    // 3. Sort by size descending (largest first)
    const sortedBySize = [...aircraftWithoutSpots].sort((a, b) => {
      const sizeA = Math.max(a.aircraft.wingspan, a.aircraft.length);
      const sizeB = Math.max(b.aircraft.wingspan, b.aircraft.length);
      return sizeB - sizeA;
    });

    // 4. Check if we have spots
    const spots = ramp.shapes?.filter((s) => s.type === "Spot") || [];
    if (spots.length === 0) {
      snackbar.notify({
        text: "No spots available on ramp",
        severity: "error",
      });
      return;
    }

    // 5. Try to park aircraft in size order
    for (const aircraft of sortedBySize) {
      try {
        const result = runIncrementalParkingOnRamp(ramp, stack, aircraft.id, {
          allowTrapping: parkingConfig.config.allowTrapping,
          egressWeight: parkingConfig.config.egressWeight,
          wasteWeight: parkingConfig.config.wasteWeight,
          structuralWeight: parkingConfig.config.structuralWeight,
          sortingStrategy: parkingConfig.config.sortingStrategy,
          zonePriorityWeight: parkingConfig.config.zonePriorityWeight,
        });

        // Check if placement was successful
        if (result.statistics.placedAircraft === 1) {
          // SUCCESS - update stack
          setStack(result.updatedStack);
          setLastResult(result);

          // Extract placement details for snackbar
          const aircraftName =
            aircraft.tail_number || aircraft.aircraft.model || "Aircraft";
          const placement = result.placement[aircraft.id];

          if (placement) {
            const spotId =
              "spotId" in placement
                ? placement.spotId
                : placement.segments[0].spotId;
            const spotShape = ramp.shapes?.find(
              (s) => s.id === spotId && s.type === "Spot"
            );
            const spotName = spotShape?.text || spotId;

            // Get zone information
            const zoneId = (spotShape?.metadata as any)?.zoneId;
            const zoneShape = zoneId
              ? ramp.shapes?.find((s) => s.id === zoneId && s.type === "Zone")
              : null;
            const zoneName =
              zoneShape?.text ||
              (zoneId && zoneId !== "default" ? zoneId : "Default Zone");

            snackbar.notify({
              text: `${aircraftName} placed in ${zoneName} → ${spotName}`,
              severity: "success",
            });
          } else {
            snackbar.notify({
              text: `${aircraftName} placed successfully`,
              severity: "success",
            });
          }

          return; // Exit after first successful placement
        }
      } catch (error) {
        console.error(
          `[Park Next] Error placing ${aircraft.tail_number}:`,
          error
        );
        // Continue to next aircraft
      }
    }

    // 6. If we get here, no aircraft could be placed
    snackbar.notify({
      text: `Unable to place any remaining aircraft (${aircraftWithoutSpots.length} without spots)`,
      severity: "warning",
    });
  }, [
    ramp,
    stack,
    setStack,
    parkingConfig.config,
    snackbar,
    setLastResult,
    handleIncrementalPark,
  ]);

  const handleTimeRangeChange = useCallback((start: Date, end: Date) => {
    setTimeRange({ start, end });
  }, []);

  const handleToggleTripSelection = useCallback((tripId: string) => {
    setSelectedTripIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tripId)) {
        newSet.delete(tripId);
      } else {
        newSet.add(tripId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAllTrips = useCallback(() => {
    const allTripIds = new Set(groupedTrips.map((t) => t.trip_id));
    setSelectedTripIds(allTripIds);
  }, [groupedTrips]);

  const handleDeselectAllTrips = useCallback(() => {
    setSelectedTripIds(new Set());
  }, []);

  const handleApplyResults = useCallback(async () => {
    // Set loading state
    setIsApplying(true);
    setApplyProgress({ current: 0, total: 0, currentItem: "" });
    
    // For testing I'm hiding the popup dialog so I can pause on a debugger
    // statement and see what's happening on the diagram
    //setLogDialogOpen(false)
    //await new Promise(resolve => setTimeout(resolve, 200))

    try {
      // Validate we have temporal results
      if (!lastResult || !("temporalData" in lastResult)) {
        console.error("[Apply Results] No temporal results available");
        snackbar.notify({
          text: "No AutoPark results to apply",
          severity: "error",
        });
        return;
      }

      // Validate required services
      if (!placementService || !postgrest) {
        console.error("[Apply Results] Missing required services");
        snackbar.notify({
          text: "Cannot apply results: service not available",
          severity: "error",
        });
        return;
      }

      // Extract data from lastResult
      const { placement, temporalData } = lastResult;
      const { spots, requests, horizonHours } = temporalData;

      // Build placement ID → request map (contains both aircraft and tail_number)
      const requestMap = new Map<string, ParkingRequest>();
      requests.forEach((req) => {
        requestMap.set(req.id, req);
      });

      // Build placement ID → trip ID map from requests (not placements state)
      const placementToTripMap = new Map<string, string>();
      requests.forEach((req) => {
        if (req.trip_id) {
          placementToTripMap.set(req.id, req.trip_id);
        }
      });

      // Convert placements to position segments using conversion utility
      const positionData = convertPlacementToPositionSegments(
        placement,
        ramp.shapes,
        ramp,
        requestMap,
        timeRange.start,
        horizonHours
      );

      // Validate no duplicate positions at same time (algorithm bug detection)
      const positionMap = new Map<string, string>();
      for (const { placementId, segments } of positionData) {
        for (const seg of segments) {
          const posKey = `${seg.x.toFixed(1)},${seg.y.toFixed(1)},${seg.startTime.getTime()}`;
          const existing = positionMap.get(posKey);
          if (existing) {
            console.warn(
              `[Apply Results] WARNING: Duplicate position detected: ${posKey} ` +
              `(placements ${existing} and ${placementId})`
            );
          }
          positionMap.set(posKey, placementId);
        }
      }

      // Track results
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Helper function to process a single trip (apply or dump)
      const processSingleTrip = async (
        tripId: string,
        displayName: string,
        dbSegments: Array<{
          start_time: string;
          end_time: string;
          ramp_id: string;
          hangar_id: null;
          x: number;
          y: number;
          angle: number;
        }> | [],
        isDump: boolean
      ): Promise<{ success: boolean; error?: string }> => {
        try {
          // Update database atomically (deletes old + inserts new)
          await placementService.rebuildTripCoverage(tripId, dbSegments);

          console.log(
            isDump
              ? `[Apply Results] ✓ Dumped unplaced trip ${tripId}`
              : `[Apply Results] ✓ Updated trip ${tripId} with ${dbSegments.length} segment(s)`
          );

          // Update progress counter after completion
          setApplyProgress(prev => ({
            current: prev.current + 1,
            total: prev.total,
            currentItem: displayName,
          }));

          return { success: true };
        } catch (error) {
          console.error(
            `[Apply Results] Failed to ${isDump ? 'dump' : 'update'} trip ${tripId}:`,
            error
          );

          // Update progress counter (count errors too)
          setApplyProgress(prev => ({
            current: prev.current + 1,
            total: prev.total,
            currentItem: displayName,
          }));

          return {
            success: false,
            error: `${isDump ? 'Dump' : 'Trip'} ${tripId}: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          };
        }
      };

      // Build set of locked trip IDs from current placements state
      const lockedTripIds = new Set<string>();
      placements?.forEach((p) => {
        if (p.is_locked) {
          lockedTripIds.add(p.trip_id);
        }
      });

      if (lockedTripIds.size > 0) {
        console.log(
          `[Apply Results] Found ${lockedTripIds.size} locked trips - will skip these`
        );
      }

      // Build set of placed trip IDs and filter out invalid placements
      const placedTripIds = new Set<string>();
      const validPlacements: typeof positionData = [];

      for (const placement of positionData) {
        const tripId = placementToTripMap.get(placement.placementId);
        if (!tripId) {
          console.error(
            `[Apply Results] CRITICAL: No trip ID found for placement ${placement.placementId}`
          );
          errors.push(`Placement ${placement.placementId}: No trip ID mapping found`);
          errorCount++;
        } else {
          placedTripIds.add(tripId);
          validPlacements.push(placement);
        }
      }

      // Calculate total operations (all requests with trip_id)
      const totalOperations = requests.filter(req => req.trip_id).length;

      // Initialize progress
      setApplyProgress({
        current: 0,
        total: totalOperations,
        currentItem: ""
      });

      // Track locked trips that were skipped
      let lockedSkipCount = 0;

      // STEP 1: Build complete segments for ALL trips (placed + unplaced periods)
      // This ensures aircraft remain "assigned to ramp" even for time periods where
      // they couldn't be placed (ramp_id set but x/y/angle null)
      const allTripPromises = requests.map(req => {
        if (!req.trip_id) {
          return null;
        }

        const tripId = req.trip_id;

        // Skip locked trips
        if (lockedTripIds.has(tripId)) {
          lockedSkipCount++;
          return null;
        }
        const displayName = req.tail_number || req.id;
        const tripStart = timeBucketToDate(req.arrivalBucket, timeRange.start);
        const tripEnd = timeBucketToDate(req.departureBucket, timeRange.start);

        // Check if this trip has placement data
        const placementData = validPlacements.find(p => p.placementId === req.id);

        let dbSegments: Array<{
          start_time: string;
          end_time: string;
          ramp_id: string;
          hangar_id: null;
          x: number | null;
          y: number | null;
          angle: number | null;
          placement_source: 'auto';
          is_locked: false;
        }> = [];

        if (placementData) {
          // Aircraft was placed for at least part of its trip
          // Convert placement segments to DB segments (with coordinates)
          const placedSegments = placementData.segments.map((seg) => ({
            start_time: seg.startTime.toISOString(),
            end_time: seg.endTime.toISOString(),
            ramp_id: ramp.id,
            hangar_id: null,
            x: seg.x,
            y: seg.y,
            angle: seg.angle,
            placement_source: 'auto' as const,
            is_locked: false,
          }));

          // Build timeline of placed periods
          const placedPeriods = placedSegments.map(seg => ({
            start: new Date(seg.start_time),
            end: new Date(seg.end_time)
          })).sort((a, b) => a.start.getTime() - b.start.getTime());

          // Fill gaps with ramp_id-only segments (assigned but not placed)
          const allSegments: typeof dbSegments = [];
          let currentTime = tripStart;

          for (const placedPeriod of placedPeriods) {
            // If there's a gap before this placed period, fill it with unplaced segment
            if (currentTime < placedPeriod.start) {
              allSegments.push({
                start_time: currentTime.toISOString(),
                end_time: placedPeriod.start.toISOString(),
                ramp_id: ramp.id,  // Assigned to ramp
                hangar_id: null,
                x: null,           // No position (couldn't be placed)
                y: null,
                angle: null,
                placement_source: 'auto' as const,
                is_locked: false,
              });
            }

            // Add the placed segment with coordinates
            const placedSegment = placedSegments.find(
              s => new Date(s.start_time).getTime() === placedPeriod.start.getTime()
            );
            if (placedSegment) {
              allSegments.push(placedSegment);
            }

            currentTime = placedPeriod.end;
          }

          // If there's a gap after all placed periods, fill it with unplaced segment
          if (currentTime < tripEnd) {
            allSegments.push({
              start_time: currentTime.toISOString(),
              end_time: tripEnd.toISOString(),
              ramp_id: ramp.id,  // Assigned to ramp
              hangar_id: null,
              x: null,           // No position (couldn't be placed)
              y: null,
              angle: null,
              placement_source: 'auto' as const,
              is_locked: false,
            });
          }

          dbSegments = allSegments;
        } else {
          // Aircraft was NOT placed at all
          // Create single segment for entire trip with ramp_id but no position
          dbSegments = [{
            start_time: tripStart.toISOString(),
            end_time: tripEnd.toISOString(),
            ramp_id: ramp.id,  // Assigned to ramp
            hangar_id: null,
            x: null,           // No position (couldn't be placed)
            y: null,
            angle: null,
            placement_source: 'auto' as const,
            is_locked: false,
          }];
        }

        return processSingleTrip(tripId, displayName, dbSegments, !placementData);
      }).filter(Boolean) as Promise<{ success: boolean; error?: string }>[];

      // Wait for all operations to complete
      const allResults = await Promise.all(allTripPromises);

      // Count results
      allResults.forEach(result => {
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
          if (result.error) {
            errors.push(result.error);
          }
        }
      });

      // Show results to user
      if (errorCount === 0) {
        // Perfect success
        const lockedMsg = lockedSkipCount > 0 ? ` (${lockedSkipCount} locked)` : '';
        snackbar.notify({
          text: `Applied ${successCount} aircraft placements to ramp${lockedMsg}`,
          severity: "success",
        });

        // Close dialog on success
        handleCloseLogDialog();

        // Trigger refresh to reload all data from database
        if (onScheduledUpdate) {
          onScheduledUpdate();
        }
      } else if (successCount > 0) {
        // Partial success
        const lockedMsg = lockedSkipCount > 0 ? `, ${lockedSkipCount} locked` : '';
        snackbar.notify({
          text: `Applied ${successCount}/${
            successCount + errorCount
          } placements (${errorCount} errors${lockedMsg})`,
          severity: "warning",
        });

        // Show first few errors
        if (errors.length > 0) {
          console.error("[Apply Results] Errors:", errors.slice(0, 5));
        }

        // Still trigger refresh for partial success
        if (onScheduledUpdate) {
          onScheduledUpdate();
        }
      } else {
        // Complete failure - show which aircraft failed
        const failedAircraft = errors
          .map(err => {
            // Try to extract tail number from error message
            const tailMatch = err.match(/([A-Z0-9]{5,})/);
            return tailMatch ? tailMatch[1] : null;
          })
          .filter(Boolean)
          .slice(0, 3);

        const errorSummary = failedAircraft.length > 0
          ? `${failedAircraft.join(", ")}${
              errorCount > 3 ? ` +${errorCount - 3} more` : ""
            }`
          : errors[0] || "Unknown error";

        snackbar.notify({
          text: `Failed to apply placements (${errorCount} errors): ${errorSummary}`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("[Apply Results] Unexpected error:", error);
      snackbar.notify({
        text: `Failed to apply results: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        severity: "error",
      });
    } finally {
      // Always clear loading state
      setIsApplying(false);
    }
  }, [
    lastResult,
    placementService,
    postgrest,
    placements,
    ramp,
    timeRange,
    snackbar,
    handleCloseLogDialog,
  ]);

  return {
    dialogOpen,
    parkingConfig,
    handleOpenDialog,
    handleCloseDialog,
    handleAutoPark,
    handleIncrementalPark,
    handleParkNext,
    logDialogOpen,
    handleOpenLogDialog,
    handleCloseLogDialog,
    lastResult,
    timeRange,
    handleTimeRangeChange,
    tripCount,
    placements,
    groupedTrips,
    selectedTripIds,
    handleToggleTripSelection,
    handleSelectAllTrips,
    handleDeselectAllTrips,
    handleApplyResults,
    isApplying,
    applyProgress,
    handleScheduleChanged,
  };
}
